/**
 * Cuaderno de requerimientos UI: nota por pantalla + elementos señalados con clic.
 * Solo memoria de la pestaña; copiar Markdown al programador / SNAT.
 */

import { ref } from 'vue';
import router from '@/router';

const MAX_ITEMS = 80;

export const REQ_NOTEBOOK_APP_LABEL = 'KOSEXA Tienda · Req. UI';

export const reqNotebookPickActive = ref(false);
export const reqNotebookScreenNote = ref('');

/** @typedef {{ id: number, routeName: string, routePath: string, selector: string, elementLabel: string, note: string }} ReqItem */
/** @type {import('vue').Ref<ReqItem[]>} */
export const reqNotebookItems = ref([]);

let _idSeq = 1;
let _pickHandler = null;
let _pickKeyHandler = null;

function cssEscapeId(id) {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(id);
  return String(id).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

export function buildRequirementSelector(el) {
  if (!el || el.nodeType !== 1) return '(elemento inválido)';
  if (el.id) return `#${cssEscapeId(el.id)}`;
  const parts = [];
  let n = el;
  let depth = 0;
  while (n && n.nodeType === 1 && depth < 8) {
    if (n.classList?.contains('req-notebook-panel') || n.classList?.contains('snat-unified-tools-panel')) break;
    let bit = (n.tagName || 'div').toLowerCase();
    if (n.classList?.length) {
      const cls = [...n.classList]
        .filter((c) => !c.startsWith('pi-') && !c.startsWith('p-') && c.length < 40)
        .slice(0, 2)
        .join('.');
      if (cls) bit += `.${cls.replace(/\s+/g, '.')}`;
    }
    parts.unshift(bit);
    n = n.parentElement;
    depth += 1;
    if (n === document.body || n === document.documentElement) break;
  }
  return parts.join(' > ') || el.tagName?.toLowerCase() || 'elemento';
}

export function buildRequirementElementLabel(el) {
  if (!el || el.nodeType !== 1) return '';
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim().slice(0, 160);
  const title = el.getAttribute('title');
  if (title) return title.trim().slice(0, 160);
  const inner = (el.textContent || '').replace(/\s+/g, ' ').trim();
  return inner.slice(0, 160) || `<${el.tagName?.toLowerCase() || 'node'}>`;
}

function teardownPickListener() {
  if (_pickHandler) {
    document.removeEventListener('click', _pickHandler, true);
    _pickHandler = null;
  }
  if (_pickKeyHandler) {
    document.removeEventListener('keydown', _pickKeyHandler, true);
    _pickKeyHandler = null;
  }
  document.body.classList.remove('req-pick-active');
}

export function startRequirementPickMode() {
  teardownPickListener();
  reqNotebookPickActive.value = true;
  document.body.classList.add('req-pick-active');

  _pickHandler = (e) => {
    if (!reqNotebookPickActive.value) return;
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (
      t.closest('.snat-unified-tools-panel') ||
      t.closest('.req-notebook-panel') ||
      t.closest('.en-trigger') ||
      t.closest('.p-sidebar') ||
      t.closest('.p-sidebar-mask')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const r = router.currentRoute.value;
    const routeName = r?.name != null ? String(r.name) : '';
    const routePath = r?.fullPath || r?.path || '';

    reqNotebookItems.value = [
      ...reqNotebookItems.value,
      {
        id: _idSeq++,
        routeName,
        routePath,
        selector: buildRequirementSelector(t),
        elementLabel: buildRequirementElementLabel(t),
        note: '',
      },
    ];
    if (reqNotebookItems.value.length > MAX_ITEMS) {
      reqNotebookItems.value = reqNotebookItems.value.slice(-MAX_ITEMS);
    }

    reqNotebookPickActive.value = false;
    teardownPickListener();
  };

  document.addEventListener('click', _pickHandler, true);

  _pickKeyHandler = (e) => {
    if (e.key === 'Escape') cancelRequirementPickMode();
  };
  document.addEventListener('keydown', _pickKeyHandler, true);
}

export function cancelRequirementPickMode() {
  reqNotebookPickActive.value = false;
  teardownPickListener();
}

export function removeRequirementItem(id) {
  reqNotebookItems.value = reqNotebookItems.value.filter((x) => x.id !== id);
}

export function clearRequirementsNotebook() {
  reqNotebookScreenNote.value = '';
  reqNotebookItems.value = [];
  cancelRequirementPickMode();
}

export function formatRequirementsNotebookMarkdown(routeNow) {
  const r = routeNow || router.currentRoute.value;
  const href = typeof window !== 'undefined' ? window.location?.href || '' : '';
  const lines = [
    '# Requerimientos UI — KOSEXA Tienda',
    '',
    'Cuaderno no persistente: se armó navegando la tienda y señalando elementos. Completar textos marcados como pendientes.',
    '',
    '## Contexto al copiar',
    '',
    `- **Aplicación:** ${REQ_NOTEBOOK_APP_LABEL}`,
    `- **URL:** \`${href.replace(/`/g, "'")}\``,
    `- **Ruta SPA actual:** \`${String(r?.fullPath || r?.path || '—').replace(/`/g, "'")}\` (${r?.name != null ? String(r.name) : '—'})`,
    `- **Generado (ISO):** ${new Date().toISOString()}`,
    '',
    '## Cambios a nivel pantalla (última nota general)',
    '',
    reqNotebookScreenNote.value.trim() || '_(Sin nota general: describa aquí el cambio global de la vista actual u omita.)_',
    '',
    '## Elementos señalados (orden de captura)',
    '',
  ];

  if (!reqNotebookItems.value.length) {
    lines.push('_(Ningún elemento capturado; use «Seleccionar elemento» y haga clic en la UI.)_', '');
  } else {
    reqNotebookItems.value.forEach((it, idx) => {
      lines.push(`### ${idx + 1}. Elemento #${it.id}`, '');
      lines.push(`- **Pantalla de captura:** \`${it.routePath}\` (${it.routeName || '—'})`);
      lines.push(`- **Selector aproximado:** \`${String(it.selector).replace(/`/g, "'")}\``);
      lines.push(`- **Texto visible (recorte):** ${JSON.stringify(it.elementLabel)}`);
      lines.push('- **Requerimiento:**');
      lines.push('');
      lines.push(it.note.trim() || '_(Pendiente de redactar.)_');
      lines.push('', '---', '');
    });
  }

  lines.push(
    '## Siguiente paso para el programador',
    '',
    '1. Priorizar por pantalla (`routePath`).',
    '2. Implementar o estimar cada ítem.',
    '3. Validar en el mismo entorno donde se capturó.',
    ''
  );

  return lines.join('\n');
}
